import Icon from '@mdi/react';
import { sendIt } from 'gatsby-plugin-purina-analytics/common/functions';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { mdiClose } from 'src/assets/icons/mdiIcons';
import { CMS_THEME } from 'src/common/enums';
import { ButtonLink } from 'src/components/Link';
import { RatingStars } from 'src/components/RatingStars/RatingStars';
import RichText from 'src/components/rich_text/RichText';
import { useDismissable } from 'src/hooks/useDismissable/useDismissable';
import { getMedia } from 'src/utils/mediaHelpers';
import { TStorageSmartAppBanner } from './types';

interface StorageSmartAppBannerProps {
  node: TStorageSmartAppBanner;
}

const StorageSmartAppBanner: React.FC<StorageSmartAppBannerProps> = ({ node }) => {
  const { t } = useTranslation();
  const { rating, title, body, component_id, bg_color } = node;
  const { isDismissed, handleDismiss } = useDismissable(
    `SmartAppBanner-${component_id}`,
    3 * 60 * 1000, // 3 minutes.
  );

  const reviewRating = rating ? parseFloat(rating) : null;

  const bgColor = () => {
    switch (bg_color) {
      case 'pink':
        return 'pds-bg-[#FBCCCC]';
      default:
        return 'pds-bg-white';
    }
  };

  const handleAnalytics = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    sendIt({
      event: 'select_content',
      eventParams: {
        link_url: event.currentTarget.href,
        content_name: node.title,
        content_type: 'smart_banner',
      },
    });
  };

  if (isDismissed) {
    return null;
  }

  return (
    <section
      aria-label="myPurina app"
      className={[
        'pds-w-full pds-border-y pds-border-b-[#dee2e6] pds-border-t-[#dee2e6]',
        bgColor(),
      ].join(' ')}
    >
      <div className="pds-m-auto pds-flex pds-max-w-[570px] pds-items-center pds-justify-between pds-gap-[1.25rem] pds-p-3">
        <div className="pds-flex pds-items-center pds-gap-[13px]">
          <button
            className="pds-border-none pds-bg-transparent"
            type="button"
            aria-label={t('Close') as string}
            onClick={handleDismiss}
          >
            <Icon path={mdiClose} size={0.6} className="pds-gray-600" />
          </button>
          {getMedia({
            alt: '',
            media: node.relationships.logo,
            className: 'pds-h-[48px] pds-w-[48px] pds-shrink-0',
            objectFit: 'contain',
            imgClassName: 'pds-object-cover',
            loading: 'eager',
          })}
          <div className="pds-flex pds-max-w-max pds-flex-col pds-gap-1">
            <div className="pds-text-md pds-font-bold pds-leading-snug">{title}</div>
            {reviewRating ? (
              <div className="pds-block sm:pds-hidden">
                <RatingStars rating={reviewRating} theme={CMS_THEME.LIGHT} starSize={0.6} />
              </div>
            ) : null}
            <RichText className="pds-mb-0 [&_>p]:pds-text-sm" body={body.processed ?? ''} />
          </div>
        </div>
        {reviewRating ? (
          <div className="pds-hidden sm:pds-block">
            <RatingStars rating={reviewRating} theme={CMS_THEME.LIGHT} />
          </div>
        ) : null}

        {node.link && node.link.url && (
          <div className="pds-shrink-0">
            {/* Desktop */}
            <ButtonLink
              to={node.link?.url}
              hideExternalIcon
              rel={node.link?.options?.attributes?.rel ?? 'noreferrer'}
              target={node.link?.options?.attributes?.target}
              buttonColor="neutral"
              className="pds-hidden sm:pds-block"
              analytics={false}
              onClick={handleAnalytics}
            >
              {node.link?.title}
            </ButtonLink>
            {/* Mobile */}
            <ButtonLink
              onClick={handleAnalytics}
              to={node.link?.url}
              hideExternalIcon
              rel={node.link?.options?.attributes?.rel ?? 'noreferrer'}
              target={node.link?.options?.attributes?.target}
              buttonColor="neutral"
              size="small"
              analytics={false}
              className="pds-block sm:pds-hidden"
            >
              {node.link?.title}
            </ButtonLink>
          </div>
        )}
      </div>
    </section>
  );
};

export default StorageSmartAppBanner;
